// This file is the entry point
import ReactDOM from "react-dom/client";
import reportWebVitals from "./reportWebVitals";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import "./index.css";
import { RouterProvider } from "react-router-dom";
import { routes } from "./routes";
import { useLocalStorage } from "usehooks-ts";
import { store } from "./redux/store";
import { Provider } from "react-redux";
import { setup as apiSetup } from "./api/api";
import { SnackbarProvider } from "notistack";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

function App() {
  const [themeVarient] = useLocalStorage("theme", "light");
  // material UI theme setup
  const light = createTheme({
    shape: {
      borderRadius: 4,
    },
    typography: {
      fontFamily: "Work Sans, sans-serif",
      fontSize: 12,
      button: {
        textTransform: "none",
      },
    },
    palette: {
      mode: "light",
      primary: {
        main: "#514eff",
      },
      secondary: {
        main: "#FD7F01",
      },
      background: {
        default: "#f3f6f9",
        paper: "#ffffff",
      },
      success: {
        main: "#03A326",
      },
      error: {
        main: "#ff4b55",
      },
      gray: {
        main: "#8B909A",
      },
      text: {
        primary: "#292D32",
      },
      white: {
        main: "#ffffff",
        contrastText: "#514eff",
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 700,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          style: {
            borderWidth: "2px",
          },
        },
        styleOverrides: {
          root: {
            fontSize: 14,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: "#292D32",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "& input:autofill": {
              boxShadow: "inset 0 0 20px 20px #ffffff !important",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: `2px`,
            },
            "&.Mui-focused": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderWidth: `2px`,
              },
            },
          },
        },
      },
    },
  });
  const dark = createTheme({
    shape: {
      borderRadius: 4,
    },
    typography: {
      fontFamily: "Work Sans, sans-serif",
      fontSize: 12,
      button: {
        textTransform: "none",
      },
    },
    palette: {
      mode: "dark",
      primary: {
        main: "#514eff",
      },
      secondary: {
        main: "#FD7F01",
      },
      background: {
        paper: "#0a0a0a",
        default: "#000000",
      },
      // success: {
      //   main: "#03A326",
      // },
      error: {
        main: "#ff4b55",
      },
      gray: {
        main: "#8B909A",
      },
      // text: {
      //   primary: "#292D32",
      // },
      white: {
        main: "#ffffff",
        contrastText: "#514eff",
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 700,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
          },
        },
      },
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          style: {
            borderWidth: "2px",
          },
        },
        styleOverrides: {
          root: {
            fontSize: 14,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: "#292D32",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            "& input:autofill": {
              boxShadow: "inset 0 0 20px 20px #18181b !important",
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: `2px`,
            },
            "&.Mui-focused": {
              "& .MuiOutlinedInput-notchedOutline": {
                borderWidth: `2px`,
              },
            },
          },
        },
      },
    },
  });
  return (
    <>
      {/* Material UI css reset */}
      <CssBaseline />
      <ThemeProvider theme={themeVarient === "light" ? light : dark}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <SnackbarProvider
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            maxSnack={8}
          />
          <Provider store={store}>
            {/* Routes from routes.js */}
            <RouterProvider router={routes} />
          </Provider>
        </LocalizationProvider>
      </ThemeProvider>
    </>
  );
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);

apiSetup(store);

reportWebVitals();
